import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, Check, ChevronLeft, ChevronRight, Clock, Loader2 } from 'lucide-react';

import { useActiveCallStore } from '@/stores/active-call.store';
import { DomainForm } from '@/features/screening/DomainForm';
import { SubmitModal } from '@/features/screening/SubmitModal';
import { HouseholdMembersStep } from '@/features/screening/HouseholdMembersStep';
import { IncompleteHmModal } from '@/features/screening/IncompleteHmModal';
import {
  DOMAIN_ORDER,
  DOMAIN_SPECS,
  defaultAnswerFor,
  isDomainValid,
  computePositiveFinding,
  computeSafetyTotal,
  type DomainAnswer,
} from '@/features/screening/domains';
import {
  useDebouncedSaveAnswer,
  useTimeEventLogger,
  useSubmitScreening,
} from '@/features/screening/useScreening';
import { screeningApi } from '@/features/screening/screening.api';
import { householdApi } from '@/features/screening/household.api';
import { useDailyStats } from '@/features/agent-metrics/useAgentMetrics';
import { useParkParent } from '@/features/queue/queueRowDetails';
import { AvisoPadreRetenido } from '@/features/queue/AvisoPadreRetenido';
import { useQuery, useMutation } from '@tanstack/react-query';
import type { HrsnDomain, SubmitScreeningInput } from '@/types/api';
import { ApiError } from '@/lib/api-client';
import { useLanguageStore } from '@/stores/language.store';
import { useUnsavedGuard } from '@/lib/useUnsavedGuard';
import { useAuthStore } from '@/stores/auth.store';
import { toast } from '@/stores/toast.store';
import { useT, type StringKey } from '@/i18n';
import { cn } from '@/lib/utils';

import { NavLayer, TintBox, BTN_PRI, BTN_SEC, BTN_GHO } from '@/components/brand/brand';
import { LangSegment } from '@/components/agent/F5Bits';

/**
 * A7 · la misma reja que el server (`NUMERIC_ID_RE` de
 * `api/src/common/pipes/numeric-id.pipe.ts`): 1 a 18 dígitos, nada más.
 */
function esIdNumerico(id: string): boolean {
  return /^[0-9]{1,18}$/.test(id);
}

/**
 * T2-C · #161 B1: el 409 del padre con familiares enviados. Nest manda el
 * objeto tal cual (`{ code, message }`); se mira también `message.code` por si
 * llega envuelto, como hace la pre-llamada (T2-A).
 */
function esPadreRetenido(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 409) return false;
  const payload = err.payload as Record<string, unknown> | null;
  const inner = payload?.message as Record<string, unknown> | string | undefined;
  const code = (typeof inner === 'object' && inner?.code) || payload?.code || null;
  return code === 'PARENT_HAS_SUBMITTED_SUBS';
}

/**
 * F5-T7 · Screening (contrato Agente-03, mockup «Agente 03 - Screening»).
 *
 * El wizard de los agentes; desde la Wave 12 también lo usa el navigator
 * EMBEBIDO en su caso (prop screeningId) — ese contrato se conserva intacto.
 *
 * MUERE (del contrato): el stepper numérico (lateral + drawer móvil) → 10
 * barras CON NOMBRE (9 dominios + Hogar), clickeables para volver a lo
 * respondido, con «Dominio N de 9» siempre también en TEXTO · el temporizador
 * que cambiaba de color → chip teal calmo SIEMPRE, semántica por rangos del
 * reporte externo (<15 · 15-20 · 20-30 · >30) anunciados UNA vez al cruzar ·
 * el tilde verde de seguridad → «sin señales» es GRIS (que no haya señales
 * todavía no es buena noticia) y la alerta dice CUÁL disparador fue
 * (respuesta de alto riesgo O puntaje — la regla A-12 viva) · «Guardar
 * borrador» no existe (nunca existió acá: el chip «Guardado» lo dice) · la
 * pantalla de resumen con progreso mensual post-envío → celebración del
 * contrato SIN UN SOLO MONTO · salir sin enviar pasa de confirm genérico a
 * un diálogo que ENUMERA qué se pierde.
 *
 * SE CONSERVA (lo vivo): autosave con debounce + flush · time-events ·
 * cancel-beacon (unmount no intencional; el de `pagehide` murió con T2-C #161:
 * recargar no borra) · rehidratación del
 * server al refrescar · SubmitModal (tiempo por categoría + ¿quiere
 * navegador?) e IncompleteHmModal (el bloqueo de miembro sin resolver con
 * sus tres salidas) · el paso de hogar completo (HouseholdMembersStep).
 *
 * GATE F-21 (offline) NO firmada: cero estado offline acá — va en F6.
 */
export function ScreeningPage({
  screeningId: screeningIdProp,
  onSubmitted,
}: { screeningId?: string; onSubmitted?: () => void } = {}): JSX.Element {
  const { screeningId: screeningIdFromUrl = '' } = useParams<{ screeningId: string }>();
  const rawScreeningId = screeningIdProp ?? screeningIdFromUrl;
  /** Embebido = alguien nos pasó el id por prop en vez de venir de la URL. */
  const embedded = screeningIdProp !== undefined;
  // A7 · un id que no es número (`/screening/12}`: un enlace mal copiado) se
  // trata como «sin id» en TODA la página: así ningún efecto lo usa — ni la
  // carga del screening, ni el latido, ni los beacons de cancelar al salir —
  // y abajo se redirige a la cola. Antes cada uno de esos pedidos llegaba al
  // SQL como `'12}'::bigint` y era un 500 en el server.
  const idInvalido = rawScreeningId !== '' && !esIdNumerico(rawScreeningId);
  const screeningId = idInvalido ? '' : rawScreeningId;
  const navigate = useNavigate();
  const t = useT();
  const lang = useLanguageStore((s) => s.lang);
  const setLang = useLanguageStore((s) => s.setLang);

  const activeCall = useActiveCallStore((s) => s.current);
  const setActiveScreeningId = useActiveCallStore((s) => s.setActiveScreeningId);
  const clearCall = useActiveCallStore((s) => s.clear);

  useEffect(() => {
    if (screeningId) setActiveScreeningId(screeningId);
  }, [screeningId, setActiveScreeningId]);

  const [answers, setAnswers] = useState<Partial<Record<HrsnDomain, DomainAnswer>>>(
    () =>
      DOMAIN_ORDER.reduce(
        (acc, d) => {
          acc[d] = defaultAnswerFor(d);
          return acc;
        },
        {} as Record<HrsnDomain, DomainAnswer>,
      ),
  );
  const [notes, setNotes] = useState<Partial<Record<HrsnDomain, string>>>({});
  const [currentDomain, setCurrentDomain] = useState<HrsnDomain>(DOMAIN_ORDER[0]!);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [stage, setStage] = useState<'domain' | 'household'>('domain');
  const [exitOpen, setExitOpen] = useState(false);
  // PD · el aviso del 409 del padre retenido, con «Dejar para después». Solo
  // existe después de ese 409.
  const [retenido, setRetenido] = useState(false);
  const intentionalExitRef = useRef(false);
  const cancelMutation = useMutation({
    mutationFn: () => {
      intentionalExitRef.current = true;
      return screeningApi.cancel(screeningId, 'button');
    },
    onSuccess: () => {
      clearCall();
      navigate('/queue', { replace: true });
    },
    onError: (err) => {
      intentionalExitRef.current = false;
      // T2-C · #161 B1: un padre con familiares ENVIADOS no se cancela (409
      // PARENT_HAS_SUBMITTED_SUBS): se avisa y se QUEDA en el wizard, para
      // terminarlo. El mismo aviso que «Volver a la cola» de la pre-llamada.
      if (esPadreRetenido(err)) {
        setExitOpen(false);
        setRetenido(true);
        toast.error(t('t2c.salir.retenidoT'), t('t2c.salir.retenidoB'));
        return;
      }
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error(
        lang === 'es' ? 'No se pudo volver a la cola' : 'Could not return to queue',
        msg,
      );
    },
  });

  // Rehidratación desde el server (refresh de página).
  const resumeQ = useQuery({
    queryKey: ['screening', screeningId],
    queryFn: () => screeningApi.get(screeningId),
    enabled: !!screeningId,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  // PD · «Dejar para después» (Edd, 25-oct-2026): el padre queda guardado, la
  // asignación vuelve a la cola (al fondo) y la agente sigue con otro paciente.
  // La asignación sale del screening cargado (o de la llamada activa).
  const park = useParkParent();
  const parkAssignmentId = resumeQ.data?.assignmentId ?? activeCall?.assignmentId ?? null;
  const dejarParaDespues = (): void => {
    if (!parkAssignmentId) return;
    intentionalExitRef.current = true;
    park.mutate(parkAssignmentId, {
      onSuccess: () => {
        clearCall();
        toast.success(t('pd.dejar.okT'), t('pd.dejar.okB'));
        navigate('/queue', { replace: true });
      },
      onError: (err) => {
        intentionalExitRef.current = false;
        toast.error(t('pd.dejar.errorT'), (err as Error).message);
      },
    });
  };
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!resumeQ.data || hydratedRef.current) return;
    hydratedRef.current = true;
    const next: Partial<Record<HrsnDomain, DomainAnswer>> = {};
    const noteMap: Partial<Record<HrsnDomain, string>> = {};
    for (const a of resumeQ.data.answers) {
      next[a.domain as HrsnDomain] = a.answer as DomainAnswer;
      if (a.notes) noteMap[a.domain as HrsnDomain] = a.notes;
    }
    if (Object.keys(next).length > 0) {
      setAnswers((prev) => ({ ...prev, ...next }));
      setNotes((prev) => ({ ...prev, ...noteMap }));
      const firstMissing = DOMAIN_ORDER.find((d) => !next[d]);
      if (firstMissing) setCurrentDomain(firstMissing);
    }
    if (resumeQ.data.startedAt) {
      setStartedAtMs(new Date(resumeQ.data.startedAt).getTime());
    }
  }, [resumeQ.data]);

  // Reloj: la línea base canónica es assignments.session_started_at →
  // screenings.started_at. Sin baseline no se dibuja (evita el 00:00 falso).
  const [startedAtMs, setStartedAtMs] = useState<number | null>(
    activeCall?.sessionStartedAt ? new Date(activeCall.sessionStartedAt).getTime() : null,
  );
  const [now, setNow] = useState<number>(Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  const elapsedSec = startedAtMs == null ? 0 : Math.max(0, Math.floor((now - startedAtMs) / 1000));

  const { schedule, flush, isSaving, lastSaveError } = useDebouncedSaveAnswer(screeningId);
  const logEvent = useTimeEventLogger(screeningId);
  const submit = useSubmitScreening(screeningId);

  useUnsavedGuard(!!screeningId && !submit.isSuccess);

  // T2-C · #161 B1 (Edd, 23-oct-2026: «Recargar no borra»). Ya NO hay beacon
  // de cancelar en `pagehide`: recargar o cerrar la pestaña deja el screening
  // abierto para retomarlo. Al recargar, la página lo rehidrata
  // (`GET /screenings/:id`, arriba); si se cerró la pestaña, «Tomar
  // siguiente» devuelve la asignación in_progress y `start()` devuelve este
  // mismo screening. Si el agente no vuelve, lo limpia el reaper a los 15 min
  // sin latido (o, si ya tiene familiares enviados, el cierre de medianoche).
  //
  // Salir a OTRA pantalla dentro de Pathway sigue cancelando (Edd): el
  // desmontaje del wizard sin salida intencional manda el cancel con
  // ?via=route_exit.
  useEffect(() => {
    return () => {
      if (!screeningId) return;
      if (intentionalExitRef.current) return;
      try {
        const st = useAuthStore.getState();
        // Sesión perdida = NO cancelar, y explícito: antes dependía de que
        // clearSession() borrara el token justo antes del desmontaje.
        if (!st.refreshToken || !st.user) return;
        const token = st.accessToken || '';
        if (!token) return;
        void fetch(`/api/screenings/${encodeURIComponent(screeningId)}/cancel?via=route_exit`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          keepalive: true,
        });
      } catch { /* best-effort */ }
    };
  }, [screeningId]);

  // ---- Lifecycle events ----
  useEffect(() => {
    if (!screeningId) return;
    void logEvent('call_started');
    void logEvent('domain_started', { domain: currentDomain });
    return () => {
      void logEvent('call_ended');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screeningId]);

  useEffect(() => {
    if (!screeningId) return;
    const handler = (): void => {
      void logEvent(document.hidden ? 'form_blurred' : 'form_focused');
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [screeningId, logEvent]);

  // ---- Handlers ----
  const updateAnswer = (next: DomainAnswer): void => {
    setAnswers((prev) => ({ ...prev, [currentDomain]: next }));
    if (isDomainValid(currentDomain, next)) {
      schedule({ domain: currentDomain, answers: next, notes: notes[currentDomain] ?? undefined });
    }
  };

  const updateNotes = (text: string): void => {
    setNotes((prev) => ({ ...prev, [currentDomain]: text }));
    const a = answers[currentDomain];
    if (a && isDomainValid(currentDomain, a)) {
      schedule({ domain: currentDomain, answers: a, notes: text });
    }
  };

  const goToDomain = (target: HrsnDomain): void => {
    if (target === currentDomain && stage === 'domain') return;
    const a = answers[currentDomain];
    if (stage === 'domain' && a && isDomainValid(currentDomain, a)) {
      void logEvent('domain_completed', { domain: currentDomain });
    }
    void logEvent('domain_started', { domain: target });
    setStage('domain');
    setCurrentDomain(target);
  };

  const goHousehold = (): void => {
    void (async () => {
      await flush();
      void logEvent('domain_completed', { domain: currentDomain });
      setStage('household');
    })();
  };

  const goNext = (): void => {
    const idx0 = DOMAIN_ORDER.indexOf(currentDomain);
    if (idx0 < DOMAIN_ORDER.length - 1) {
      goToDomain(DOMAIN_ORDER[idx0 + 1]!);
    } else {
      goHousehold();
    }
  };

  const goPrev = (): void => {
    const idx0 = DOMAIN_ORDER.indexOf(currentDomain);
    if (idx0 > 0) goToDomain(DOMAIN_ORDER[idx0 - 1]!);
  };

  const openSubmitModal = async (): Promise<void> => {
    await flush();
    void logEvent('submit_clicked');
    setSubmitOpen(true);
  };

  const [showSummary, setShowSummary] = useState(false);

  // v0.6.0 Fase 11.12 — bloqueo de miembros sin resolver, con sus 3 salidas.
  const [incompleteHmModalState, setIncompleteHmModalState] = useState<{
    open: boolean;
    pending: Array<{ hmId: string; fullName: string; status: 'pending' | 'in_progress' }>;
    retryInput: SubmitScreeningInput | null;
  }>({ open: false, pending: [], retryInput: null });

  const onConfirmSubmit = async (input: SubmitScreeningInput): Promise<void> => {
    try {
      await flush();
      await submit.mutateAsync(input);
      void logEvent('screening_completed');
      intentionalExitRef.current = true;
      setSubmitOpen(false);

      if (embedded) {
        onSubmitted?.();
        return;
      }

      clearCall();
      // Swap silencioso de URL para que atrás no reviva un wizard enviado.
      // F5-R9a: a '/' (Inicio absorbió la cola) — así Atrás no cae en el
      // redirect de /queue.
      window.history.replaceState({}, '', '/');
      setShowSummary(true);
    } catch (e) {
      const payload = (e instanceof ApiError ? e.payload : null) as
        | {
            code?: string;
            incompleteMembers?: Array<{ hmId: string; fullName: string; status: 'pending' | 'in_progress' }>;
          }
        | null;
      if (payload?.code === 'INCOMPLETE_HOUSEHOLD_MEMBERS' && Array.isArray(payload.incompleteMembers)) {
        setSubmitOpen(false);
        setIncompleteHmModalState({
          open: true,
          pending: payload.incompleteMembers,
          retryInput: input,
        });
        return;
      }
    }
  };

  const validDomains = useMemo(() => {
    const s = new Set<HrsnDomain>();
    for (const d of DOMAIN_ORDER) {
      const a = answers[d];
      if (a && isDomainValid(d, a)) s.add(d);
    }
    return s;
  }, [answers]);

  const positiveDomains = useMemo(() => {
    const s = new Set<HrsnDomain>();
    for (const d of DOMAIN_ORDER) {
      const a = answers[d];
      if (a && isDomainValid(d, a) && computePositiveFinding(d, a)) s.add(d);
    }
    return s;
  }, [answers]);

  // A-12 (HIPAA hotfix 2026-05-13): la alerta mira LOS DOS disparadores —
  // respuesta de alto riesgo (physical_hurt / threaten en fairly_often o
  // frequently) O puntaje agregado >= 11 — y dice cuál fue.
  const safetyTrigger = useMemo((): 'none' | 'response' | 'score' => {
    const sa = answers.safety;
    if (!sa) return 'none';
    const isHighRisk = (v: unknown): boolean => v === 'fairly_often_4' || v === 'frequently_5';
    if (isHighRisk(sa.physical_hurt) || isHighRisk(sa.threaten)) return 'response';
    if (computeSafetyTotal(sa) >= 11) return 'score';
    return 'none';
  }, [answers.safety]);

  const allValid = validDomains.size === DOMAIN_ORDER.length;
  const idx = DOMAIN_ORDER.indexOf(currentDomain);

  // A7 · URL con id roto → a la cola (Inicio), reemplazando la entrada del
  // historial para que «atrás» no vuelva a la URL rota. Desde ahí «Tomar
  // siguiente» retoma el screening en curso si lo había (take-next devuelve
  // la asignación in_progress). Embebido no redirige: el id lo pasa el código
  // del caso, no el usuario, y cae en el aviso de abajo.
  if (idInvalido && !embedded) {
    return <Navigate to="/queue" replace />;
  }

  if (!screeningId) {
    return (
      <TintBox tone="red" className="max-w-[560px] mx-auto">
        <p className="font-head text-[13px] font-bold m-0 mb-0.5">Missing screening id</p>
        <p className="m-0">Return to the queue and start again.</p>
      </TintBox>
    );
  }

  if (showSummary) {
    return (
      <BrandCelebration
        screeningId={screeningId}
        firstName={activeCall?.client.firstName ?? null}
        positiveCount={positiveDomains.size}
        onNext={() => navigate('/queue', { replace: true, state: { justCompleted: true } })}
        t={t}
      />
    );
  }

  const prevName = idx > 0 ? t(STEP_KEYS[DOMAIN_ORDER[idx - 1]!]) : null;
  const nextName =
    idx < DOMAIN_ORDER.length - 1 ? t(STEP_KEYS[DOMAIN_ORDER[idx + 1]!]) : t('agentF5.sc.stepHome');

  return (
    <div className="max-w-[1080px] mx-auto font-body text-nav-navy">
      <div className="bg-nav-soft border-[1.5px] border-nav-card rounded-[14px] px-4 sm:px-7 pt-6 pb-[34px] animate-fade-up-slow">
        <div className="max-w-[790px] mx-auto">
          {/* Barra superior: identidad · reloj calmo · seguridad · idioma */}
          <div className="sticky top-16 z-10 flex items-center gap-3 flex-wrap min-h-[76px] bg-white border-[1.5px] border-nav-card rounded-xl px-5 py-3.5 mb-[18px]">
            <div className="min-w-0 flex-1">
              {activeCall ? (
                <>
                  <p className="font-head text-base font-extrabold tracking-[-0.01em] m-0 truncate">
                    {activeCall.client.firstName} {activeCall.client.lastName}
                  </p>
                  <p className="text-[12.5px] text-nav-slate tabular-nums m-0">
                    ID <span className="font-mono">{activeCall.client.cin}</span>
                  </p>
                </>
              ) : (
                <p className="text-sm text-nav-slate m-0">Screening</p>
              )}
            </div>
            {startedAtMs != null && <TimerChip elapsedSec={elapsedSec} t={t} />}
            <SafetyChip trigger={safetyTrigger} t={t} />
            <LangSegment
              value={lang}
              options={[
                { v: 'es' as const, label: 'Español' },
                { v: 'en' as const, label: 'English' },
              ]}
              onPick={(l) => setLang(l)}
            />
            <SaveChip saving={isSaving} error={lastSaveError} t={t} />
          </div>

          {lastSaveError && (
            <TintBox tone="amber" className="mb-3.5">
              <p className="m-0 text-[13px]">
                {t('agentF5.sc.saveFail')} — {lastSaveError.message}
              </p>
            </TintBox>
          )}

          {/* PD · el aviso del 409 (padre con familiares enviados) con su
              salida explícita. No en el wizard embebido del navigator: el
              park es de la cola del agente. */}
          {retenido && !embedded && (
            <AvisoPadreRetenido
              testId="pd-aviso-wizard"
              titulo={t('t2c.salir.retenidoT')}
              cuerpo={t('t2c.salir.retenidoB')}
              onDejar={dejarParaDespues}
              pendiente={park.isPending}
              deshabilitado={!parkAssignmentId}
            />
          )}

          {/* Progreso: 10 barras con nombre (9 dominios + Hogar) */}
          <ProgressBars
            stage={stage}
            currentDomain={currentDomain}
            validDomains={validDomains}
            allValid={allValid}
            onDomain={goToDomain}
            onHousehold={goHousehold}
            t={t}
          />

          {stage === 'domain' ? (
            <div key={currentDomain} className="animate-fade-up">
              {/* «Dominio N de 9» también en TEXTO: el agente al teléfono dice
                  el número, no cuenta barras. */}
              <p className="font-head text-[11.5px] font-bold tracking-[.04em] text-nav-slate m-0 mb-3.5">
                {t('agentF5.sc.domainLine', {
                  n: idx + 1,
                  title: (lang === 'es'
                    ? DOMAIN_SPECS[currentDomain].titleEs
                    : DOMAIN_SPECS[currentDomain].title
                  ).toLocaleLowerCase(),
                })}
              </p>

              <div className="bg-white border-[1.5px] border-nav-card rounded-xl px-6 sm:px-7 py-[26px]">
                <DomainForm
                  domain={currentDomain}
                  answer={answers[currentDomain] ?? defaultAnswerFor(currentDomain)}
                  onChange={updateAnswer}
                  notes={notes[currentDomain] ?? ''}
                  onNotesChange={updateNotes}
                />
              </div>

              {/* Navegación: el siguiente NOMBRA el dominio que viene. */}
              <div className="flex items-center gap-2.5 flex-wrap mt-[18px]">
                <button
                  type="button"
                  className={cn(BTN_SEC, 'min-h-[44px]')}
                  onClick={goPrev}
                  disabled={idx === 0}
                >
                  <ChevronLeft className="h-4 w-4" aria-hidden />
                  {prevName ?? t(STEP_KEYS[DOMAIN_ORDER[0]!])}
                </button>
                <span className="flex-1" />
                <button
                  type="button"
                  className={cn(BTN_GHO, 'min-h-[44px] px-3')}
                  onClick={() => setExitOpen(true)}
                >
                  {t('agentF5.sc.exit')}
                </button>
                <button
                  type="button"
                  className={cn(BTN_PRI, 'min-h-[44px]')}
                  onClick={goNext}
                >
                  {t('agentF5.sc.next', { name: nextName })}
                  <ChevronRight className="h-4 w-4" aria-hidden />
                </button>
              </div>
            </div>
          ) : (
            <div className="animate-fade-up">
              <div className="bg-white border-[1.5px] border-nav-card rounded-xl px-6 sm:px-7 py-[26px]">
                <HouseholdMembersStep
                  screeningId={screeningId}
                  onContinue={() => void openSubmitModal()}
                  onBack={() => setStage('domain')}
                />
              </div>
              <div className="flex justify-end mt-[18px]">
                <button
                  type="button"
                  className={cn(BTN_GHO, 'min-h-[44px] px-3')}
                  onClick={() => setExitOpen(true)}
                >
                  {t('agentF5.sc.exit')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Revisión antes de enviar — lo vivo (tiempo por categoría + ¿quiere
          navegador?); su re-vestido a la familia de modales queda anotado. */}
      <SubmitModal
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        answers={answers}
        elapsedSec={elapsedSec}
        isSubmitting={submit.isPending}
        submitError={submit.error as Error | null}
        onConfirm={onConfirmSubmit}
        screeningId={screeningId}
      />

      <IncompleteHmModal
        open={incompleteHmModalState.open}
        parentScreeningId={screeningId}
        pendingMembers={incompleteHmModalState.pending}
        onClose={() => setIncompleteHmModalState({ open: false, pending: [], retryInput: null })}
        onCompleteRequest={() => {
          setIncompleteHmModalState({ open: false, pending: [], retryInput: null });
          const el = document.querySelector('[data-testid="household-list"]')
            ?? document.querySelector('[data-hm-section]');
          if (el) {
            (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }}
        onAllDeclined={() => {
          const input = incompleteHmModalState.retryInput;
          setIncompleteHmModalState({ open: false, pending: [], retryInput: null });
          if (input) {
            void onConfirmSubmit(input);
          }
        }}
      />

      {/* Salir sin enviar = CANCELA: enumera exactamente qué se pierde; el
          primario es SEGUIR. */}
      <NavLayer
        open={exitOpen}
        onClose={() => setExitOpen(false)}
        kicker={t('agentF5.sc.exit')}
        title={t('agentF5.sc.exTitle')}
      >
        <TintBox tone="red" className="mb-3.5">
          <p className="font-head font-bold text-[10.5px] tracking-[.12em] uppercase m-0 mb-2 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" aria-hidden />
            {t('agentF5.sc.exKicker')}
          </p>
          <ul className="m-0 pl-4 text-sm leading-[1.7]">
            <li>{t('agentF5.sc.exDomains', { n: validDomains.size })}</li>
            <li>{t('agentF5.sc.exMembers')}</li>
            <li>{t('agentF5.sc.exMinutes', { m: Math.max(1, Math.floor(elapsedSec / 60)) })}</li>
          </ul>
        </TintBox>
        <p className="text-[13.5px] text-nav-slate m-0 mb-4">
          {t('agentF5.sc.exFoot', { name: activeCall?.client.firstName ?? '—' })}
        </p>
        <div className="flex justify-end gap-2.5 flex-wrap">
          <button
            type="button"
            className={BTN_PRI}
            onClick={() => setExitOpen(false)}
            disabled={cancelMutation.isPending}
          >
            {t('agentF5.sc.exStay')}
          </button>
          <button
            type="button"
            onClick={() => cancelMutation.mutate()}
            disabled={cancelMutation.isPending}
            className="inline-flex items-center justify-center gap-2 rounded-lg border-[1.5px] border-nav-red bg-nav-red text-white font-head font-semibold text-sm px-4 py-2 min-h-[44px] cursor-pointer hover:opacity-90 disabled:opacity-45 transition-colors"
          >
            {cancelMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
            {t('agentF5.sc.exLeave')}
          </button>
        </div>
      </NavLayer>
    </div>
  );
}

// ---------------------------------------------------------------------------

const STEP_KEYS: Record<HrsnDomain, StringKey> = {
  living_situation: 'agentF5.sc.stepLiving',
  housing_conditions: 'agentF5.sc.stepConditions',
  utilities: 'agentF5.sc.stepUtilities',
  food: 'agentF5.sc.stepFood',
  transportation: 'agentF5.sc.stepTransport',
  career: 'agentF5.sc.stepCareer',
  school: 'agentF5.sc.stepSchool',
  safety: 'agentF5.sc.stepSafety',
  physical_ability: 'agentF5.sc.stepPhysical',
};

/**
 * Las 10 barras con nombre. Cada paso es un BOTÓN de 44px: los dominios
 * respondidos (y el actual) se puede volver a tocar; Hogar solo se habilita
 * con los 9 válidos (el mismo gate del «Siguiente» final).
 */
function ProgressBars({
  stage,
  currentDomain,
  validDomains,
  allValid,
  onDomain,
  onHousehold,
  t,
}: {
  stage: 'domain' | 'household';
  currentDomain: HrsnDomain;
  validDomains: Set<HrsnDomain>;
  allValid: boolean;
  onDomain: (d: HrsnDomain) => void;
  onHousehold: () => void;
  t: ReturnType<typeof useT>;
}): JSX.Element {
  const steps: Array<{ id: string; label: string; domain: HrsnDomain | null }> = [
    ...DOMAIN_ORDER.map((d) => ({ id: d as string, label: t(STEP_KEYS[d]), domain: d as HrsnDomain | null })),
    { id: 'household', label: t('agentF5.sc.stepHome'), domain: null },
  ];
  return (
    <div className="flex items-start gap-1 mb-6" role="list">
      {steps.map((s) => {
        const isHome = s.domain === null;
        const isActive = isHome ? stage === 'household' : stage === 'domain' && s.domain === currentDomain;
        const isDone = isHome ? false : validDomains.has(s.domain!) && !isActive;
        const clickable = isHome ? allValid && stage !== 'household' : validDomains.has(s.domain!) || isActive;
        return (
          <button
            key={s.id}
            type="button"
            role="listitem"
            title={s.label}
            aria-current={isActive ? 'step' : undefined}
            disabled={!clickable && !isActive}
            onClick={() => {
              if (isHome) onHousehold();
              else if (s.domain) onDomain(s.domain);
            }}
            className={cn(
              'flex-1 min-h-[44px] px-0.5 flex flex-col items-stretch justify-start bg-transparent border-0 cursor-pointer',
              'disabled:cursor-default',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-nav-teal',
            )}
          >
            <span
              aria-hidden="true"
              className={cn(
                'block h-[3px] rounded-[2px] transition-colors duration-300',
                isActive ? 'bg-nav-navy' : isDone ? 'bg-nav-teal' : 'bg-[#dde3e7]',
              )}
            />
            <span
              className={cn(
                'block mt-[7px] font-head text-[10.5px] truncate text-center',
                isActive ? 'font-extrabold text-nav-navy' : 'font-semibold text-nav-slate',
              )}
            >
              {s.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Reloj calmo: teal SIEMPRE, jamás rojo, sin cambio de color con el tiempo.
 * Semántica por rangos del reporte externo (<15 · 15-20 · 20-30 · >30): al
 * cruzar un rango lo dice UNA vez y sigue.
 */
function TimerChip({ elapsedSec, t }: { elapsedSec: number; t: ReturnType<typeof useT> }): JSX.Element {
  const min = Math.floor(elapsedSec / 60);
  const rangeIdx = min < 15 ? 0 : min < 20 ? 1 : min < 30 ? 2 : 3;
  const rangeKey = (['agentF5.sc.r1', 'agentF5.sc.r2', 'agentF5.sc.r3', 'agentF5.sc.r4'] as const)[rangeIdx]!;
  const [announced, setAnnounced] = useState<string>('');
  const lastIdxRef = useRef(rangeIdx);
  useEffect(() => {
    if (rangeIdx !== lastIdxRef.current) {
      lastIdxRef.current = rangeIdx;
      setAnnounced(t('agentF5.sc.rangeEnter', { range: t(rangeKey) }));
      const h = window.setTimeout(() => setAnnounced(''), 6000);
      return () => window.clearTimeout(h);
    }
    return undefined;
  }, [rangeIdx, rangeKey, t]);
  const mm = Math.floor(elapsedSec / 60);
  const ss = elapsedSec % 60;
  return (
    <span className="inline-flex items-center gap-2">
      <span className="inline-flex items-center gap-1.5 px-3.5 py-[7px] rounded-full bg-nav-tealTint border border-[#c9e2e2] font-head text-[13px] font-extrabold tabular-nums text-nav-tealDark">
        <Clock className="h-[15px] w-[15px]" aria-hidden />
        {mm}:{String(ss).padStart(2, '0')}
      </span>
      <span aria-live="polite" className={cn('text-[11.5px] text-nav-slate', !announced && 'sr-only')}>
        {announced}
      </span>
    </span>
  );
}

/**
 * Indicador de seguridad: «sin señales» en GRIS (no salvia — todavía no hay
 * señales no es buena noticia); la alerta es roja y DICE cuál disparador fue.
 */
function SafetyChip({
  trigger,
  t,
}: {
  trigger: 'none' | 'response' | 'score';
  t: ReturnType<typeof useT>;
}): JSX.Element {
  if (trigger === 'none') {
    return (
      <span className="inline-flex items-center gap-1.5 px-3.5 py-[7px] rounded-full bg-nav-hair border border-nav-card font-head text-[11.5px] font-bold text-nav-slate">
        <AlertTriangle className="h-[15px] w-[15px]" aria-hidden />
        {t('agentF5.sc.safeNone')}
      </span>
    );
  }
  return (
    <span
      role="status"
      className="inline-flex items-center gap-1.5 px-3.5 py-[7px] rounded-full bg-nav-redTint border border-[#e3b5ae] font-head text-[11.5px] font-bold text-nav-red"
    >
      <AlertTriangle className="h-[15px] w-[15px]" aria-hidden />
      {trigger === 'response' ? t('agentF5.sc.safeResp') : t('agentF5.sc.safeScore')}
    </span>
  );
}

/** «Guardado» — no hay botón de guardar; el chip lo dice. */
function SaveChip({
  saving,
  error,
  t,
}: {
  saving: boolean;
  error: Error | null;
  t: ReturnType<typeof useT>;
}): JSX.Element {
  if (saving) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-nav-slate">
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
        {t('agentF5.sc.saving')}
      </span>
    );
  }
  if (error) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-nav-red">
        <AlertTriangle className="h-3 w-3" aria-hidden />
        {t('agentF5.sc.saveFail')}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-nav-slate">
      <Check className="h-3 w-3" aria-hidden />
      {t('agentF5.sc.saved')}
    </span>
  );
}

/**
 * Celebración del contrato: check salvia · «Enviado» · cifras SIN UN SOLO
 * MONTO (screenings de hoy · dominios positivos · miembros esperando subida)
 * · «Tomar el siguiente». Reemplaza la pantalla de resumen con progreso
 * mensual — la estimación de bono removida por privacidad NO VUELVE.
 */
function BrandCelebration({
  screeningId,
  firstName,
  positiveCount,
  onNext,
  t,
}: {
  screeningId: string;
  firstName: string | null;
  positiveCount: number;
  onNext: () => void;
  t: ReturnType<typeof useT>;
}): JSX.Element {
  const dailyQ = useDailyStats();
  const hhQ = useQuery({
    queryKey: ['screening', screeningId, 'household-summary'],
    queryFn: () => householdApi.getHouseholdSummary(screeningId),
    staleTime: 60_000,
  });
  const submitted = dailyQ.data?.submittedToday ?? null;
  const target = dailyQ.data?.target ?? null;
  const awaiting = (hhQ.data?.members ?? []).filter(
    (m) => m.subScreeningId != null && m.subIsSubmitted === true && !m.subFindhelpUploadedAt,
  ).length;

  return (
    <div className="max-w-[600px] mx-auto text-center font-body text-nav-navy py-10">
      <span className="sr-only" aria-live="polite">
        {submitted != null && target != null
          ? t('agentF5.sc.doneSr', { n: submitted, target })
          : t('agentF5.sc.doneTitle')}
      </span>
      <div
        className="w-16 h-16 mx-auto rounded-full bg-nav-sageTint text-nav-sageText flex items-center justify-center animate-fade-up-slow"
        aria-hidden="true"
      >
        <Check className="h-[30px] w-[30px]" strokeWidth={2.4} />
      </div>
      <h1
        className="font-head text-[28px] font-extrabold tracking-[-0.02em] mt-5 mb-1 animate-fade-up-slow"
        style={{ animationDelay: '60ms' }}
      >
        {t('agentF5.sc.doneTitle')}
      </h1>
      <p
        className="text-[14.5px] text-nav-slate m-0 animate-fade-up-slow"
        style={{ animationDelay: '120ms' }}
      >
        {t('agentF5.sc.doneSub', { name: firstName ?? '—' })}
      </p>
      <div
        className="grid gap-3.5 mt-7 animate-fade-up-slow"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', animationDelay: '180ms' }}
      >
        <div className="bg-white border-[1.5px] border-nav-card rounded-xl px-5 py-[18px]">
          <p className="font-head text-[26px] font-extrabold tabular-nums m-0 leading-none">
            {submitted != null && target != null ? `${submitted} / ${target}` : '—'}
          </p>
          <p className="text-[12.5px] text-nav-slate mt-2 m-0">{t('agentF5.sc.stat1')}</p>
        </div>
        <div className="bg-white border-[1.5px] border-nav-card rounded-xl px-5 py-[18px]">
          <p className="font-head text-[26px] font-extrabold tabular-nums m-0 leading-none">{positiveCount}</p>
          <p className="text-[12.5px] text-nav-slate mt-2 m-0">{t('agentF5.sc.stat2')}</p>
        </div>
        <div className="bg-white border-[1.5px] border-nav-card rounded-xl px-5 py-[18px]">
          <p className="font-head text-[26px] font-extrabold tabular-nums m-0 leading-none">
            {hhQ.isLoading ? '—' : awaiting}
          </p>
          <p className="text-[12.5px] text-nav-slate mt-2 m-0">{t('agentF5.sc.stat3')}</p>
        </div>
      </div>
      <button type="button" className={cn(BTN_PRI, 'mt-8 min-h-[52px] px-6 text-[15px]')} onClick={onNext}>
        {t('agentF5.sc.doneCta')}
      </button>
    </div>
  );
}
